/**
 * Live rent for one apartment in one calendar month.
 *
 * The Revenue tab used to show a single, purely contractual number: the sum of
 * room prices. That is what the apartment SHOULD earn — it says nothing about
 * what actually arrived. This hook supplies the other half, so "expected" and
 * "live" can sit side by side:
 *
 *   • the month is the one canonical rent window used everywhere else —
 *     `payments.type = 'rent'` with `due_date` inside the calendar month
 *     (see ensureRentPaymentForMonth, which enforces one row per month);
 *   • "paid" is `isPaymentEffectivelyPaid`, not `status = 'paid'` — a manual
 *     row counts only when a proof or an admin attestation backs it, exactly
 *     as the tenant profile, the deposit gate and every rollup already count;
 *   • bookings are filtered by `admin_archived_at`, never by status: rent for
 *     August belongs to whoever lived there in August, including a tenant who
 *     has since moved out;
 *   • rooms are counted alongside the money (`paidRooms` / `billedRooms`),
 *     because a per-room management fee is earned per ROOM collected — the
 *     live half of `computeApartmentEconomics` bills off that count.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isPaymentEffectivelyPaid, latestProofByPaymentId } from "@/lib/rent";
import { nextMonthStart } from "@/lib/payment-booking";

/** One rent row for the month, with the tenant and room it belongs to. */
export type ApartmentMonthRentRow = {
  paymentId: string;
  bookingId: number | null;
  roomId: number | null;
  tenantName: string | null;
  amount: number;
  /** Settled under isPaymentEffectivelyPaid — not merely `status = 'paid'`. */
  paid: boolean;
  /** Raw payments.status, for the "paid but unbacked" case the predicate rejects. */
  status: string | null;
  dueDate: string | null;
};

export type ApartmentMonthRent = {
  /** Rent (EUR) whose payment row counts as effectively paid for the month. */
  paidRent: number;
  /** Rent (EUR) billed for the month at all — paid or still open. */
  billedRent: number;
  /** Tenants whose rent for the month is settled. */
  paidTenants: number;
  /** Tenants billed for the month (rent rows exist), paid or not. */
  billedTenants: number;
  /**
   * Distinct ROOMS whose rent for the month is settled — the live basis for a
   * per-room management fee. Rooms rather than tenants because the fee is
   * charged per room, and a room re-let mid-month can carry two bookings.
   */
  paidRooms: number;
  /** Distinct rooms billed for the month, paid or not. */
  billedRooms: number;
  /** The rows behind the totals, for the per-tenant breakdown dialog. */
  rows: ApartmentMonthRentRow[];
};

export const EMPTY_APARTMENT_MONTH_RENT: ApartmentMonthRent = {
  paidRent: 0,
  billedRent: 0,
  paidTenants: 0,
  billedTenants: 0,
  paidRooms: 0,
  billedRooms: 0,
  rows: [],
};

export type ApartmentMonthPaymentRow = {
  id: string;
  booking_id: number | null;
  amount_cents: number | null;
  status: string | null;
  due_date: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  marked_paid_at: string | null;
  marked_paid_by: string | null;
};

type ApartmentMonthBooking = {
  id: number;
  room_id: number | null;
  name: string | null;
};

/**
 * Turns rent-payment rows into the totals + per-row breakdown the Revenue tab
 * renders. Pulled out of the query function so the money math (which rows
 * count as paid, the cents totals, the distinct-tenant counts) is testable
 * without mocking supabase/react-query.
 */
export function aggregateApartmentMonthRent(
  payments: ApartmentMonthPaymentRow[],
  bookingById: Map<number, ApartmentMonthBooking>,
  proofByPayment: Map<string, { status: string | null }>
): ApartmentMonthRent {
  const paidBookings = new Set<number>();
  const billedBookings = new Set<number>();
  const paidRoomIds = new Set<number>();
  const billedRoomIds = new Set<number>();
  const rows: ApartmentMonthRentRow[] = [];
  let paidCents = 0;
  let billedCents = 0;
  for (const p of payments) {
    const cents = Number(p.amount_cents) || 0;
    const paid = isPaymentEffectivelyPaid(p, (id) => proofByPayment.get(id));
    const booking = p.booking_id != null ? bookingById.get(p.booking_id) : null;
    billedCents += cents;
    if (p.booking_id != null) billedBookings.add(p.booking_id);
    if (booking?.room_id != null) billedRoomIds.add(booking.room_id);
    if (paid) {
      paidCents += cents;
      if (p.booking_id != null) paidBookings.add(p.booking_id);
      if (booking?.room_id != null) paidRoomIds.add(booking.room_id);
    }
    rows.push({
      paymentId: p.id,
      bookingId: p.booking_id,
      roomId: booking?.room_id ?? null,
      tenantName: booking?.name ?? null,
      amount: cents / 100,
      paid,
      status: p.status,
      dueDate: p.due_date,
    });
  }

  return {
    paidRent: paidCents / 100,
    billedRent: billedCents / 100,
    paidTenants: paidBookings.size,
    billedTenants: billedBookings.size,
    paidRooms: paidRoomIds.size,
    billedRooms: billedRoomIds.size,
    rows,
  };
}

/**
 * @param roomIds The apartment's visible rooms. Empty → the query is skipped.
 * @param month   Calendar month as `YYYY-MM`.
 */
export function useApartmentMonthRent(roomIds: number[], month: string) {
  // Sorted so a re-render that reorders the rooms doesn't refetch.
  const key = [...roomIds].sort((a, b) => a - b).join(",");
  return useQuery({
    queryKey: ["v2", "apartment-month-rent", key, month],
    enabled: roomIds.length > 0 && /^\d{4}-\d{2}$/.test(month),
    staleTime: 60_000,
    queryFn: async (): Promise<ApartmentMonthRent> => {
      const { data: bookingRows, error: bErr } = await supabase
        .from("bookings")
        .select("id, room_id, name")
        .in("room_id", roomIds)
        .is("admin_archived_at", null);
      if (bErr) throw bErr;
      const bookings = (bookingRows ?? []) as Array<{
        id: number;
        room_id: number | null;
        name: string | null;
      }>;
      const bookingById = new Map(bookings.map((b) => [b.id, b]));
      const bookingIds = bookings.map((b) => b.id);
      if (bookingIds.length === 0) return { ...EMPTY_APARTMENT_MONTH_RENT };

      const { data: paymentRows, error: pErr } = await supabase
        .from("payments")
        .select(
          "id, booking_id, amount_cents, status, due_date, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by"
        )
        .in("booking_id", bookingIds)
        .eq("type", "rent")
        .gte("due_date", `${month}-01`)
        .lt("due_date", nextMonthStart(month));
      if (pErr) throw pErr;
      const payments = (paymentRows ?? []) as ApartmentMonthPaymentRow[];
      if (payments.length === 0) return { ...EMPTY_APARTMENT_MONTH_RENT };

      // Latest proof per payment backs the manual-paid half of the predicate.
      const { data: proofRows, error: prErr } = await supabase
        .from("payment_proofs")
        .select("payment_id, status, created_at")
        .in(
          "payment_id",
          payments.map((p) => p.id)
        )
        .order("created_at", { ascending: false });
      if (prErr) throw prErr;
      const proofByPayment = latestProofByPaymentId(
        (proofRows ?? []) as Array<{
          payment_id: string;
          status: string | null;
        }>
      );

      return aggregateApartmentMonthRent(payments, bookingById, proofByPayment);
    },
  });
}

/** `YYYY-MM` of today, in local time (the month an admin means by "this month"). */
export function currentMonthKey(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** The `count` most recent months, newest first, as `YYYY-MM`. */
export function recentMonthKeys(
  count: number,
  now: Date = new Date()
): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push(
      currentMonthKey(new Date(now.getFullYear(), now.getMonth() - i, 1))
    );
  }
  return out;
}

/** "August 2026" for a `YYYY-MM` key. */
export function monthKeyLabel(month: string): string {
  const year = Number(month.slice(0, 4));
  const monthIndex0 = Number(month.slice(5, 7)) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex0)) return month;
  return new Date(year, monthIndex0, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
}
